/**
 * ActionExecutor — TF-209 / PF-014
 *
 * Executes step-level actions returned by the evaluate-step API.
 * Actions are executed in order after the experience renders.
 *
 * MVP Actions:
 *   - go_to_url — Navigate to a specified URL
 *   - show_leads_popup — Show a leads capture popup (email collection)
 *   - display_offer — Display an offer popup (inline or template-based)
 *
 * PF-014: When any popup action carries a `template_id` in its config, the
 * template's page_content is fetched from the API, rendered via BlockRenderer,
 * and presented inside the shared overlay/modal shell. If the fetch fails or
 * no template_id is present, the existing inline popup is shown as fallback.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useFunnelAnalytics } from '../analytics';
import { useFunnelContext } from '../funnel-provider';
import type { FunnelOffer, StepPageBlock, TemplateContentResponse, RuntimeStepAction, ActionConfig } from '../types';
import { normalizeBlocks, renderBlocks } from './BlockRenderer';
import type { BlockCallbacks } from './ComponentRenderers';
import { funnelLogger } from '../logger';

// OAF-027/030: StepAction and ActionConfig now imported from types.ts — single source of truth.

interface ActionExecutorProps {
  actions: RuntimeStepAction[];
  /** Lifecycle mode from the funnel — controls how often actions fire. */
  triggerLifecycle?: string;
  /** Unique step ID to track execution per-step. */
  stepId: string;
  /** Deprecated compatibility prop. Cart mutations are owned by higher-level flows. */
  onAddToCart?: (productId: string, variantId: string, quantity: number) => void | Promise<void>;
  /** Callback when a displayed offer is accepted. */
  onOfferAccept?: (offerId: string) => void | Promise<void>;
  /** Callback when a displayed offer is declined. */
  onOfferDecline?: (offerId: string) => void | Promise<void>;
  /** Active offer fallback for display_offer popups when inline copy is omitted. */
  fallbackOffer?: Partial<FunnelOffer> | null;
}

const DEPRECATED_FREE_GIFT_POPUP_ACTION = 'show_free_gift_popup';
const DEPRECATED_FREE_GIFT_POPUP_MESSAGE =
  'Free-gift popups must be authored as campaigns (type=freegift_popup). Funnel action `show_free_gift_popup` is no longer supported.';

function resolveActionTemplateId(config: ActionConfig): string | undefined {
  if (typeof config.template_id === 'string' && config.template_id.trim()) {
    return config.template_id;
  }
  const camel = (config as Record<string, unknown>).templateId;
  if (typeof camel === 'string' && camel.trim()) {
    return camel;
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Lifecycle helpers
// ---------------------------------------------------------------------------

function getStorageKey(stepId: string, actionId: string): string {
  return `funnel_action_${stepId}_${actionId}`;
}

function isDevelopmentRuntime(): boolean {
  return Boolean(import.meta.env?.DEV || process.env.NODE_ENV === 'development');
}

function hasActionFired(stepId: string, actionId: string, lifecycle: string): boolean {
  if (isDevelopmentRuntime()) {
    return false;
  }
  // 'unlimited', 'always', 'page' — always show, never mark as fired
  if (lifecycle === 'unlimited' || lifecycle === 'always' || lifecycle === 'page') {
    return false;
  }
  const key = getStorageKey(stepId, actionId);
  if (lifecycle === 'lifetime' || lifecycle === 'once_ever') {
    return localStorage.getItem(key) === 'fired';
  }
  if (lifecycle === 'session' || lifecycle === 'once_per_session') {
    return sessionStorage.getItem(key) === 'fired';
  }
  if (lifecycle === 'once_per_day') {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    return localStorage.getItem(key) === today;
  }
  return false;
}

function markActionFired(stepId: string, actionId: string, lifecycle: string): void {
  if (isDevelopmentRuntime()) {
    return;
  }
  // 'unlimited', 'always', 'page' — no persistence
  if (lifecycle === 'unlimited' || lifecycle === 'always' || lifecycle === 'page') {
    return;
  }
  const key = getStorageKey(stepId, actionId);
  if (lifecycle === 'lifetime' || lifecycle === 'once_ever') {
    localStorage.setItem(key, 'fired');
  } else if (lifecycle === 'session' || lifecycle === 'once_per_session') {
    sessionStorage.setItem(key, 'fired');
  } else if (lifecycle === 'once_per_day') {
    localStorage.setItem(key, new Date().toISOString().slice(0, 10));
  }
}

// ---------------------------------------------------------------------------
// Popup Components (inline — minimal UI for MVP)
// ---------------------------------------------------------------------------

function LeadsPopup({
  onClose,
  endpoint,
  onSubmitted,
  config,
}: {
  onClose: () => void;
  endpoint?: string;
  onSubmitted?: () => void;
  config?: ActionConfig;
}) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const title = typeof config?.title === 'string' && config.title.trim() ? config.title : 'Stay in the loop';
  const description =
    typeof config?.description === 'string' && config.description.trim()
      ? config.description
      : 'Enter your email to get exclusive offers and updates.';
  const submitLabel =
    typeof (config as Record<string, unknown> | undefined)?.cta_text === 'string' &&
    String((config as Record<string, unknown>).cta_text).trim()
      ? String((config as Record<string, unknown>).cta_text)
      : 'Subscribe';
  const declineLabel =
    typeof (config as Record<string, unknown> | undefined)?.decline_text === 'string' &&
    String((config as Record<string, unknown>).decline_text).trim()
      ? String((config as Record<string, unknown>).decline_text)
      : 'No thanks';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitError(null);
    if (endpoint) {
      try {
        await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
      } catch (err) {
        funnelLogger.error('[ActionExecutor] Leads submit failed', err instanceof Error ? err : undefined);
        setSubmitError('We could not submit your email right now. Please try again.');
        return;
      }
    }
    setSubmitted(true);
    onSubmitted?.();
    setTimeout(onClose, 2000);
  };

  return (
    <div style={overlayStyle}>
      <div style={popupStyle}>
        {submitted ? (
          <p style={{ textAlign: 'center', fontSize: '16px' }}>Thanks for subscribing!</p>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 style={{ margin: '0 0 12px', fontSize: '18px' }}>{title}</h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#666' }}>
              {description}
            </p>
            {submitError && (
              <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#b42318' }}>
                {submitError}
              </p>
            )}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              style={inputStyle}
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <button type="submit" style={ctaButtonStyle}>{submitLabel}</button>
              <button type="button" onClick={onClose} style={dismissButtonStyle}>{declineLabel}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function UnsupportedFreeGiftActionNotice({ onClose }: { onClose: () => void }) {
  return (
    <div style={overlayStyle}>
      <div style={popupStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: '18px' }}>Unsupported funnel action</h3>
        <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#666' }}>
          {DEPRECATED_FREE_GIFT_POPUP_MESSAGE}
        </p>
        <button type="button" onClick={onClose} style={dismissButtonStyle}>Dismiss</button>
      </div>
    </div>
  );
}

function DisplayOfferPopup({
  onClose,
  onAccept,
  config,
  fallbackOffer,
}: {
  onClose: () => void;
  onAccept: () => void;
  config: ActionConfig;
  fallbackOffer?: Partial<FunnelOffer> | null;
}) {
  const headline = config.offer_headline ?? fallbackOffer?.headline ?? 'Special Offer';
  const imageUrl = config.offer_image_url ?? fallbackOffer?.imageUrl;
  const offerName = config.offer_name ?? fallbackOffer?.name;
  const ctaText = config.offer_cta_text ?? fallbackOffer?.ctaText ?? 'Yes, I want this!';
  const declineText = config.offer_decline_text ?? fallbackOffer?.declineText ?? 'No thanks';

  return (
    <div style={overlayStyle}>
      <div style={popupStyle}>
        {imageUrl && (
          <img
            src={imageUrl}
            alt={offerName ?? 'Special offer'}
            style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', marginBottom: '12px', borderRadius: '8px' }}
          />
        )}
        <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>
          {headline}
        </h3>
        {config.offer_discount_label && (
          <span style={{
            display: 'inline-block', background: '#dc2626', color: '#fff',
            padding: '4px 10px', borderRadius: '4px', fontSize: '13px', fontWeight: 700,
            marginBottom: '12px',
          }}>
            {config.offer_discount_label}
          </span>
        )}
        {offerName && (
          <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#666' }}>
            {offerName}
          </p>
        )}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onClick={onAccept} style={ctaButtonStyle}>
            {ctaText}
          </button>
          <button type="button" onClick={onClose} style={dismissButtonStyle}>
            {declineText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Minimal inline styles for MVP popups
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const popupStyle: React.CSSProperties = {
  background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '400px',
  width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '6px',
  fontSize: '14px', boxSizing: 'border-box',
};
const ctaButtonStyle: React.CSSProperties = {
  flex: 1, padding: '10px 16px', background: '#111', color: '#fff', border: 'none',
  borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
};
const dismissButtonStyle: React.CSSProperties = {
  padding: '10px 16px', background: 'transparent', color: '#666', border: '1px solid #ddd',
  borderRadius: '6px', fontSize: '14px', cursor: 'pointer',
};

// ---------------------------------------------------------------------------
// PF-014: Template-based popup
// ---------------------------------------------------------------------------

/**
 * useTemplateBlocks — fetches and caches template page_content blocks.
 *
 * Returns:
 *  - `blocks`  — the resolved StepPageBlock[], empty until fetch resolves
 *  - `loading` — true while the fetch is in-flight
 *  - `failed`  — true when the fetch rejected (triggers fallback to inline popup)
 */
function useTemplateBlocks(
  templateId: string | undefined,
  fetchTemplate: (id: string) => Promise<TemplateContentResponse>,
): {
  blocks: StepPageBlock[];
  loading: boolean;
  failed: boolean;
} {
  const [blocks, setBlocks] = useState<StepPageBlock[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!templateId) {
      setBlocks([]);
      setFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    fetchTemplate(templateId)
      .then((data) => {
        if (cancelled) return;
        const resolved = normalizeBlocks(data.pageContent);
        if (resolved.length === 0) {
          if (isDevelopmentRuntime()) {
            funnelLogger.warn('ActionExecutor template fallback: empty template content', {
              templateId,
            });
          }
          setBlocks([]);
          setFailed(true);
        } else {
          if (isDevelopmentRuntime()) {
            funnelLogger.info('ActionExecutor template loaded', {
              templateId,
              blockCount: resolved.length,
            });
          }
          setBlocks(resolved);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (isDevelopmentRuntime()) {
          funnelLogger.warn('ActionExecutor template fallback: template fetch failed', {
            templateId,
            error: err instanceof Error ? err.message : String(err),
          });
        }
        setBlocks([]);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [templateId, fetchTemplate]);

  return { blocks, loading, failed };
}

/**
 * TemplatePopup — renders template blocks inside the standard overlay/modal shell.
 * Falls back to the `fallback` element if blocks is empty or template loading failed.
 */
function TemplatePopup({
  templateId,
  fetchTemplate,
  onClose,
  onAccept,
  onDecline,
  fallback,
  fallbackOffer,
}: {
  templateId: string;
  fetchTemplate: (id: string) => Promise<TemplateContentResponse>;
  onClose: () => void;
  onAccept?: () => void;
  onDecline?: () => void;
  fallback: React.ReactElement;
  fallbackOffer?: Partial<FunnelOffer> | null;
}): React.ReactElement {
  const callbacks: BlockCallbacks = { onAccept, onDecline };
  const { blocks, loading, failed } = useTemplateBlocks(templateId, fetchTemplate);

  // While loading, render nothing — avoid layout shift (the inline popup will
  // show after fallback is triggered on failure, or template renders on success).
  if (loading) return <></>;

  // Failed fetch or empty template → use the inline fallback popup
  if (failed || blocks.length === 0) return fallback;

  // Build a minimal FunnelOffer for blocks that reference offer data
  const emptyOffer: FunnelOffer = {
    id: '',
    name: '',
    productId: '',
    variantId: '',
    headline: '',
    description: '',
    imageUrl: '',
    ctaText: 'OK',
    declineText: 'No thanks',
    pricingType: 'fixed',
    pricingValue: null,
    originalPrice: null,
  };

  const resolvedOffer: FunnelOffer = {
    ...emptyOffer,
    ...fallbackOffer,
  };

  const renderedBlocks = renderBlocks(blocks, resolvedOffer, callbacks);

  return (
    <div style={overlayStyle}>
      <div style={{ ...popupStyle, maxWidth: '560px', position: 'relative' }}>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          style={{
            position: 'absolute' as const,
            top: '12px',
            right: '12px',
            background: 'transparent',
            border: 'none',
            fontSize: '20px',
            cursor: 'pointer',
            lineHeight: 1,
            color: '#666',
          }}
        >
          ×
        </button>
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '12px', marginTop: '8px' }}>
          {renderedBlocks}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionExecutor Component
// ---------------------------------------------------------------------------

export function ActionExecutor({ actions, triggerLifecycle = 'session', stepId, onOfferAccept, onOfferDecline, fallbackOffer }: ActionExecutorProps) {
  const { fetchTemplate } = useFunnelContext();
  const analytics = useFunnelAnalytics();
  const executedRef = useRef(false);
  // TF-AUD-003: Track last stepId so executedRef resets on downsell chaining.
  const lastStepRef = useRef<string | null>(null);
  const [activePopup, setActivePopup] = useState<'leads' | 'offer' | 'unsupported-free-gift' | null>(null);
  const [popupConfig, setPopupConfig] = useState<ActionConfig>({});

  const closePopup = useCallback(() => setActivePopup(null), []);

  useEffect(() => {
    // TF-AUD-003: Reset on stepId change so downsell/chained step actions fire.
    if (lastStepRef.current !== stepId) {
      executedRef.current = false;
      lastStepRef.current = stepId;
      setActivePopup(null);
      setPopupConfig({});
    }
    if (executedRef.current || actions.length === 0) return;
    executedRef.current = true;

    // One AbortController per effect invocation — aborted on unmount or re-run.
    const abort = new AbortController();

    // Execute actions in order (after experience renders)
    let popupShown = false;
    let navigated = false; // TF-AUDIT-005: track navigation to stop loop + reach shared cleanup
    for (const action of actions) {
      if (popupShown || navigated) break; // Stop after popup or navigation
      if (hasActionFired(stepId, action.id, triggerLifecycle)) continue;

      if (String(action.type) === DEPRECATED_FREE_GIFT_POPUP_ACTION) {
        markActionFired(stepId, action.id, triggerLifecycle);
        funnelLogger.warn(DEPRECATED_FREE_GIFT_POPUP_MESSAGE, {
          actionId: action.id,
          stepId,
        });
        setPopupConfig(action.config);
        setActivePopup('unsupported-free-gift');
        popupShown = true;
        continue;
      }

      switch (action.type) {
        case 'go_to_url': {
          const url = action.config.url;
          if (!url) break;
          markActionFired(stepId, action.id, triggerLifecycle);
          if (action.config.open_in_new_tab) {
            window.open(url, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = url;
          }
          navigated = true;
          break;
        }
        case 'show_leads_popup':
          markActionFired(stepId, action.id, triggerLifecycle);
          analytics.trackPopupShown({ stepId, actionType: 'show_leads_popup', actionId: action.id });
          setPopupConfig(action.config);
          setActivePopup('leads');
          popupShown = true;
          break;
        case 'dispatch_event': {
          const eventName = action.config.event_name ? String(action.config.event_name) : action.label;
          if (eventName) {
            window.dispatchEvent(new CustomEvent(eventName, { detail: action.config }));
          }
          markActionFired(stepId, action.id, triggerLifecycle);
          break;
        }
        case 'save_to_storage': {
          const storageKey = action.config.key ? String(action.config.key) : action.label;
          const storageValue = action.config.value != null ? String(action.config.value) : 'true';
          const storageTarget = String(action.config.storage ?? 'local');
          if (storageKey) {
            if (storageTarget === 'session') {
              sessionStorage.setItem(storageKey, storageValue);
            } else {
              localStorage.setItem(storageKey, storageValue);
            }
          }
          markActionFired(stepId, action.id, triggerLifecycle);
          break;
        }
        case 'connect_to_system': {
          const endpoint = action.config.endpoint;
          if (endpoint) {
            const method = action.config.method ?? 'POST';
            const maxRetries = Number(action.config.retry_count ?? 2);
            const timeoutMs = Number(action.config.timeout_ms ?? 10000);
            const authHeader = action.config.auth_header;
            const headers: Record<string, string> = { 'Content-Type': 'application/json' };
            if (authHeader) {
              headers['Authorization'] = String(authHeader);
            }

            const attemptFetch = async (attempt: number): Promise<void> => {
              if (abort.signal.aborted) return;
              try {
                // Per-attempt timeout controller chained to the effect-level abort.
                const timeoutController = new AbortController();
                const timer = setTimeout(() => timeoutController.abort(), timeoutMs);
                // Combine effect abort with per-attempt timeout via listener.
                abort.signal.addEventListener('abort', () => timeoutController.abort(), { once: true });
                await fetch(String(endpoint), {
                  method,
                  headers,
                  // TF-AUD-013: Send only stepId + actionId + explicit payload, not internal config fields.
                  body: method !== 'GET' ? JSON.stringify({ stepId, actionId: action.id, payload: action.config.payload ?? {} }) : undefined,
                  signal: timeoutController.signal,
                });
                clearTimeout(timer);
              } catch (err) {
                if (abort.signal.aborted) return; // Component unmounted — stop silently.
                if (attempt < maxRetries) {
                  const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
                  await new Promise<void>((r) => {
                    const t = setTimeout(r, delay);
                    abort.signal.addEventListener('abort', () => { clearTimeout(t); r(); }, { once: true });
                  });
                  return attemptFetch(attempt + 1);
                }
                funnelLogger.error('[ActionExecutor] connect_to_system failed after retries', err instanceof Error ? err : undefined, {
                  attempts: maxRetries + 1,
                  actionId: action.id,
                  stepId,
                });
              }
            };

            void attemptFetch(0);
          }
          markActionFired(stepId, action.id, triggerLifecycle);
          break;
        }
        case 'display_offer': {
          markActionFired(stepId, action.id, triggerLifecycle);
          analytics.trackPopupShown({ stepId, actionType: 'display_offer', actionId: action.id });
          setPopupConfig(action.config);
          setActivePopup('offer');
          popupShown = true;
          break;
        }
        default:
          funnelLogger.warn('Unknown funnel action type', {
            actionType: action.type,
            actionId: action.id,
            stepId,
          });
      }
    }

    return () => { abort.abort(); };
  }, [actions, stepId, triggerLifecycle, analytics]);

  if (activePopup === 'unsupported-free-gift') {
    return <UnsupportedFreeGiftActionNotice onClose={closePopup} />;
  }

  if (activePopup === 'leads') {
    const popupTemplateId = resolveActionTemplateId(popupConfig);
    const inlineLeads = (
      <LeadsPopup
        onClose={closePopup}
        endpoint={popupConfig.endpoint}
        config={popupConfig}
        onSubmitted={() => analytics.trackLeadsSubmitted({ stepId, actionId: '' })}
      />
    );
    if (popupTemplateId) {
      return (
        <TemplatePopup
          templateId={popupTemplateId}
          fetchTemplate={fetchTemplate}
          onClose={closePopup}
          onAccept={closePopup}
          onDecline={closePopup}
          fallback={inlineLeads}
        />
      );
    }
    return inlineLeads;
  }

  if (activePopup === 'offer') {
    const popupTemplateId = resolveActionTemplateId(popupConfig);
    const effectiveOfferId = fallbackOffer?.id;
    const handleOfferAccept = async () => {
      try {
        if (effectiveOfferId && onOfferAccept) {
          await onOfferAccept(effectiveOfferId);
        }
      } catch {
        return;
      }
      closePopup();
    };
    const handleOfferDecline = async () => {
      try {
        if (effectiveOfferId && onOfferDecline) {
          await onOfferDecline(effectiveOfferId);
        }
      } catch {
        // Closing a popup is a local UI action and must not depend on the
        // remote decline callback succeeding.
      }
      closePopup();
    };
    const inlineOffer = (
      <DisplayOfferPopup
        config={popupConfig}
        fallbackOffer={fallbackOffer}
        onAccept={handleOfferAccept}
        onClose={handleOfferDecline}
      />
    );
    if (popupTemplateId) {
      return (
        <TemplatePopup
          templateId={popupTemplateId}
          fetchTemplate={fetchTemplate}
          onClose={handleOfferDecline}
          onAccept={handleOfferAccept}
          onDecline={handleOfferDecline}
          fallback={inlineOffer}
          fallbackOffer={fallbackOffer}
        />
      );
    }
    return inlineOffer;
  }

  return null;
}
